// G4 Rule-Group Accordion (Sprint 4, 2026-05-29).
//
// The medical-toxicology G4 ships 210 decisions; R4 (undefined term)
// alone accounts for 66 of them (31%). Without grouping, the
// architect clicks through each decision sequentially. With
// rule-grouping, they fix R4 at the source (the glossary) once,
// then accept all 66 R4 rewrites in one click.
//
// This component is the grouping layer that wraps the existing
// G4 decision rendering. It's deliberately ignorant of the
// decision-rendering primitive (Q9FindingPanel, fallback card,
// etc.) — the parent passes a render function for each decision,
// so this component never has to know how to draw a decision.
//
// Per-group bulk-approve reuses the parent's existing
// auto-approve discipline (Rewriter-status guardrail: skip
// `unresolved` / `needs_review`) — the parent computes the
// approval candidates and passes a callback.

import { useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  CheckCheck,
  ChevronDown,
  ChevronRight,
  Sparkles,
} from 'lucide-react'

import type { GateDecisionRecord } from '@/types'

// Severity ordering used by the parent's renderer; mirrored here so
// the group header can colour-match the surrounding cards.
type SeverityClass = 'critical' | 'high' | 'suggested' | 'other'

function classifySeverity(d: GateDecisionRecord): SeverityClass {
  const payload = d.payload as Record<string, unknown> | null | undefined
  const sev = String((payload?.['severity'] ?? '') || '').toLowerCase()
  if (sev === 'critical') return 'critical'
  if (sev === 'high') return 'high'
  if (sev === 'suggested') return 'suggested'
  return 'other'
}

const SEVERITY_CHIP_CLASS: Record<SeverityClass, string> = {
  critical: 'alm-chip-warn',
  high: 'alm-chip-warn',
  suggested: 'alm-chip-info',
  other: 'alm-chip',
}

/**
 * Pull the rule code (e.g. ``R4``, ``C2``) from a decision's payload.
 * Returns null when the decision has no rule (manual G4-D# question,
 * prevention finding, anything without a rule field).
 */
export function getDecisionRule(d: GateDecisionRecord): string | null {
  const payload = d.payload as Record<string, unknown> | null | undefined
  const raw = payload?.['rule']
  if (typeof raw === 'string' && raw.trim()) return raw.trim().toUpperCase()
  return null
}

export interface RuleGroup {
  /** ``"R4"``, ``"C2"``, or ``"_ungrouped"`` for decisions with no rule. */
  ruleCode: string
  /** Human-readable label for the group header (e.g. ``"R4 — Undefined term"``). */
  label: string
  /** Decisions in this group, in their original order. */
  decisions: GateDecisionRecord[]
  /** Predominant severity used for the header chip's colour. */
  severity: SeverityClass
}

/**
 * Group decisions by rule code. Sort groups by:
 *   1. Severity bucket (critical → high → suggested → other)
 *   2. Pending count descending (largest backlog first within bucket)
 *   3. Rule code alphabetically (stable secondary sort)
 *
 * Decisions with no rule code land in a single ``_ungrouped`` bucket
 * at the very end so the architect doesn't lose them.
 */
export function groupDecisionsByRule(
  decisions: GateDecisionRecord[],
): RuleGroup[] {
  const byRule = new Map<string, GateDecisionRecord[]>()
  for (const d of decisions) {
    const key = getDecisionRule(d) ?? '_ungrouped'
    if (!byRule.has(key)) byRule.set(key, [])
    byRule.get(key)!.push(d)
  }
  const groups: RuleGroup[] = []
  for (const [ruleCode, members] of byRule) {
    // The group's "predominant severity" is the most severe one
    // present — critical > high > suggested > other.
    let sev: SeverityClass = 'other'
    for (const d of members) {
      const c = classifySeverity(d)
      if (c === 'critical') {
        sev = 'critical'
        break
      }
      // 'critical' is impossible here (we'd have broken above), so
      // narrowing reduces this to upgrade-or-stay for 'high' and
      // suggested-only-if-still-other for 'suggested'.
      if (c === 'high') sev = 'high'
      else if (c === 'suggested' && sev === 'other') sev = 'suggested'
    }
    const label =
      ruleCode === '_ungrouped'
        ? 'Other decisions (no rule)'
        : ruleCode
    groups.push({
      ruleCode,
      label,
      decisions: members,
      severity: sev,
    })
  }

  const sevOrder: Record<SeverityClass, number> = {
    critical: 0,
    high: 1,
    suggested: 2,
    other: 3,
  }
  // Pending count drives within-severity ordering.
  const pendingCount = (g: RuleGroup) =>
    g.decisions.filter((d) => d.status === 'pending').length

  groups.sort((a, b) => {
    if (sevOrder[a.severity] !== sevOrder[b.severity]) {
      return sevOrder[a.severity] - sevOrder[b.severity]
    }
    const pa = pendingCount(a)
    const pb = pendingCount(b)
    if (pa !== pb) return pb - pa
    // Push the "no-rule" bucket to the absolute end
    if (a.ruleCode === '_ungrouped') return 1
    if (b.ruleCode === '_ungrouped') return -1
    return a.ruleCode.localeCompare(b.ruleCode)
  })

  return groups
}

// --------------------------------------------------------------------------- //
// Accordion component
// --------------------------------------------------------------------------- //
export interface G4RuleGroupAccordionProps {
  groups: RuleGroup[]
  /** Renders one decision card; supplied by the parent so this
   *  component stays ignorant of Q9 / fallback rendering details. */
  renderDecision: (decision: GateDecisionRecord) => ReactNode
  /** Bulk-approve callback per group; the parent applies the same
   *  Rewriter-status guardrail as Auto-Approve All. */
  onApproveGroup: (ruleCode: string) => void
  /** Groups smaller than this auto-expand on mount; larger ones
   *  collapse so a 66-row R4 group doesn't paint at once. */
  autoExpandThreshold?: number
}

export function G4RuleGroupAccordion({
  groups,
  renderDecision,
  onApproveGroup,
  autoExpandThreshold = 5,
}: G4RuleGroupAccordionProps) {
  const initialExpanded = useMemo(() => {
    const set = new Set<string>()
    for (const g of groups) {
      if (g.decisions.length <= autoExpandThreshold) set.add(g.ruleCode)
    }
    return set
  }, [groups, autoExpandThreshold])

  const [expanded, setExpanded] = useState<Set<string>>(initialExpanded)

  // When the set of groups changes (e.g. after Submit & Reevaluate
  // produces a new batch), re-seed the expanded set so groups that
  // are now small auto-expand.
  useEffect(() => {
    setExpanded(initialExpanded)
  }, [initialExpanded])

  const toggle = (ruleCode: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(ruleCode)) next.delete(ruleCode)
      else next.add(ruleCode)
      return next
    })

  return (
    <div className="flex flex-col gap-3">
      {groups.map((g) => {
        const isOpen = expanded.has(g.ruleCode)
        const pending = g.decisions.filter(
          (d) => d.status === 'pending',
        ).length
        const decided = g.decisions.length - pending
        return (
          <div
            key={g.ruleCode}
            className="rounded-md border border-slate-700/50 bg-slate-900/40 overflow-hidden"
            data-testid={`g4-rule-group-${g.ruleCode}`}
          >
            <div className="flex items-center gap-2 px-3 py-2 bg-slate-900/60">
              <button
                type="button"
                onClick={() => toggle(g.ruleCode)}
                className="flex items-center gap-1.5 text-slate-200 hover:text-white"
                aria-expanded={isOpen}
                aria-controls={`g4-group-body-${g.ruleCode}`}
              >
                {isOpen ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
                <span className="font-mono text-sm font-semibold">
                  {g.label}
                </span>
              </button>
              <span className={`alm-chip ${SEVERITY_CHIP_CLASS[g.severity]}`}>
                {g.decisions.length}{' '}
                {g.decisions.length === 1 ? 'finding' : 'findings'}
              </span>
              {decided > 0 && (
                <span className="alm-chip">
                  {decided} / {g.decisions.length} decided
                </span>
              )}
              <div className="flex-1" />
              {pending > 0 && g.ruleCode !== '_ungrouped' && (
                <button
                  type="button"
                  onClick={() => onApproveGroup(g.ruleCode)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-emerald-700/40 bg-emerald-950/30 px-2.5 py-1 text-xs text-emerald-200 hover:bg-emerald-950/60"
                  title={`Approve every pending ${g.ruleCode} finding (Rewriter-status guardrail: unresolved / needs_review rows are skipped).`}
                  data-testid={`g4-rule-group-approve-${g.ruleCode}`}
                >
                  <CheckCheck className="h-3.5 w-3.5" />
                  Approve {pending} pending
                </button>
              )}
            </div>
            {isOpen && (
              <div
                id={`g4-group-body-${g.ruleCode}`}
                className="flex flex-col gap-3 p-3"
              >
                {g.decisions.map((d) => (
                  <div key={d.id}>{renderDecision(d)}</div>
                ))}
              </div>
            )}
          </div>
        )
      })}
      {groups.length === 0 && (
        <div className="rounded-md border border-slate-700/50 bg-slate-900/40 p-4 text-center text-sm text-slate-400">
          <Sparkles className="inline-block h-4 w-4 mr-1.5 text-slate-500" />
          No findings to review at the current filter.
        </div>
      )}
    </div>
  )
}
